import { CalcElement, shownParams } from "../calc-result";
import { liveCalc } from "../calc-result-read";
/* eslint-disable camelcase */
/* eslint-disable new-cap */

import {
  appearanceEnum,
  opsMissionTypeEnum,
  parameterStruct,
  payloadCodeTypeEnum,
  perfResultsEnum,
  processResultStateEnum,
  property,
  targetPlatformEnum,
} from "../dataStruct";
import {
  alignMarkdownTablesInDocument,
  getParameterOfArray,
  getParametersArray,
  getParametersArrayBulk,
  getUtcTime,
  markDownProcessing,
  padLeft,
  parseCss,
  round,
  svgDiagramDataStruct,
  svgLineStruct,
} from "../functions";
import { aircraftStruct, fppProvider, payLoadStruct } from "./providers/fppProvider";

export class loadAndBalanceReport {
  constructor() {}

  // markdown report of aircraft
  async createLoadAndBalanceMarkdown(
    acft: aircraftStruct,
    flightId: string,
    opsMissionType: opsMissionTypeEnum,
    classCom: any,
    givenLoadingParams: parameterStruct[] | undefined,
    givenElements: CalcElement[]
  ): Promise<string> {
    // One read per (flight, suffix) scope instead of one per parameter.
    const flightlistParams = await getParametersArray(flightId, "flightlist", opsMissionTypeEnum.all);
    const loadingParams = givenLoadingParams ?? (await getParametersArray(flightId, "loading", opsMissionType));
    const mdText_lst: string[] = [];

    mdText_lst.push("## MASS AND BALANCE");
    mdText_lst.push(
      "Flight **{FlightNumber}** -  Ops Mission Type: **{opsMissionType}** - revision {revision} @  {issueTime} \n"
    );
    mdText_lst.push(
      "ETD **{estTimeDep_gui_UTC} / {estTimeDep_gui_LT}**:   **{originName}** to **{destName}**,  Alternate {altName}\n"
    );
    mdText_lst.push("_____________");
    mdText_lst.push("|>");
    mdText_lst.push("### Loading / Components Breakdown");
    mdText_lst.push("Aircraft:");
    mdText_lst.push("- A/C Empty Mass       : {aircraftEmptyMass} kg");
    mdText_lst.push("- Dry Oper. Mass       : {dom} kg");
    mdText_lst.push("");
    mdText_lst.push("Crew / Pax / Cargo:");
    mdText_lst.push("- Crew Mass (excl CC)  : {crewMassExclCrewCargo} kg");
    mdText_lst.push("- Pax Mass             : {paxWeight} kg");
    mdText_lst.push("- Cargo (inc. CC)      : {cargoWeightIncCrewCargo} kg");
    mdText_lst.push("");
    mdText_lst.push("Operational Scalars:");
    mdText_lst.push("- Takeoff Mass         : {takeoffMass} kg");
    mdText_lst.push("- Landing Mass         : {landingMass} kg");
    mdText_lst.push("- Zero Fuel Mass       : {zeroFuelMass} kg");
    mdText_lst.push("- Empty Eqpt. Mass     : {emptyEquippedMass} kg");

    mdText_lst.push("|<");

    const elementTypes = [
      payloadCodeTypeEnum.equipment,
      payloadCodeTypeEnum.optionalEquipment,
      payloadCodeTypeEnum.fuel,
      payloadCodeTypeEnum.pax,
      payloadCodeTypeEnum.cargo,
      payloadCodeTypeEnum.crew,
      payloadCodeTypeEnum.pic,
      payloadCodeTypeEnum.fo,
      payloadCodeTypeEnum.crewCargo,
    ];
    // The calculation passes the elements of its run. The report reads no stored loading.
    const [installedEquipment, optionalEquipment, fuelTanks, pax, cargo, crew, pic, fo, crewC] = this.loadingObjectsOf(
      givenElements,
      flightId,
      elementTypes
    );
    mdText_lst.push("");

    mdText_lst.push("### Aircraft Equipment Loaded");
    let acftEquMassSum: number = 0;
    let acftEquMomxSum: number = 0;
    let acftEquMomySum: number = 0;
    mdText_lst.push("| ITEM          | MASS [kg] | MOMENT X [kg x m] | MOMENT Y [kg x m] | ");
    mdText_lst.push("|---------------|:---------:|:---------:|:---------:|");
    for (let i = 0; i < installedEquipment.length; i++) {
      mdText_lst.push(
        "| " +
          installedEquipment[i].name +
          "| " +
          round(installedEquipment[i].weightValue, 1) +
          " | " +
          round(installedEquipment[i].loadArmX * installedEquipment[i].weightValue, 1) +
          " | " +
          round(installedEquipment[i].loadArmY * installedEquipment[i].weightValue, 1) +
          " | "
      );

      acftEquMassSum += installedEquipment[i].weightValue;
      acftEquMomxSum += installedEquipment[i].loadArmX * installedEquipment[i].weightValue;
      acftEquMomySum += installedEquipment[i].loadArmY * installedEquipment[i].weightValue;
    }
    mdText_lst.push("|  |  |  |  |");
    mdText_lst.push(
      "| SUM          | **" +
        round(acftEquMassSum, 0) +
        " kg** | **" +
        round(acftEquMomxSum, 0) +
        " kg x m** | **" +
        round(acftEquMomySum, 0) +
        " kg x m** | "
    );
    mdText_lst.push("");

    mdText_lst.push("");
    mdText_lst.push("|>");

    mdText_lst.push("### Longitudinal Loading Diagram");
    mdText_lst.push("[]fppDiagr:" + acft.model + ":" + perfResultsEnum.withinLoadAndBalanceEnvelope_longitudinal);
    mdText_lst.push("### Lateral Loading Diagram");
    mdText_lst.push("[]fppDiagr:" + acft.model + ":" + perfResultsEnum.withinLoadAndBalanceEnvelope_lateral);
    mdText_lst.push("|<");

    mdText_lst.push("### Optional Aircraft Equipment Loaded");
    let acftOptEquMassSum: number = 0;
    let acftOptEquMomxSum: number = 0;
    let acftOptEquMomySum: number = 0;
    mdText_lst.push("| ITEM          | MASS [kg] | MOMENT X [kg x m] | MOMENT Y [kg x m] | ");
    mdText_lst.push("|---------------|:---------:|:---------:|:---------:|");
    for (let i = 0; i < optionalEquipment.length; i++) {
      mdText_lst.push(
        "| " +
          optionalEquipment[i].name +
          "| " +
          round(optionalEquipment[i].weightValue, 1) +
          " | " +
          round(optionalEquipment[i].loadArmX * optionalEquipment[i].weightValue, 1) +
          " | " +
          round(optionalEquipment[i].loadArmY * optionalEquipment[i].weightValue, 1) +
          " | "
      );

      acftOptEquMassSum += optionalEquipment[i].weightValue;
      acftOptEquMomxSum += optionalEquipment[i].loadArmX * optionalEquipment[i].weightValue;
      acftOptEquMomySum += optionalEquipment[i].loadArmY * optionalEquipment[i].weightValue;
    }
    mdText_lst.push("|  |  |  |  |");
    mdText_lst.push(
      "| SUM          | **" +
        round(acftOptEquMassSum, 1) +
        " kg** | **" +
        round(acftOptEquMomxSum, 0) +
        " kg x m** | **" +
        round(acftOptEquMomySum, 0) +
        " kg x m** | "
    );

    mdText_lst.push("");

    mdText_lst.push("### Fuel Loading");
    let fuelEquMassSum: number = 0;
    let fuelEquMomxSum: number = 0;
    let fuelEquMomySum: number = 0;
    mdText_lst.push("| ITEM          | MASS [kg] | MOMENT X [kg x m] | MOMENT Y [kg x m] | ");
    mdText_lst.push("|---------------|:---------:|:---------:|:---------:|");
    for (let i = 0; i < fuelTanks.length; i++) {
      mdText_lst.push(
        "| " +
          fuelTanks[i].name +
          "| " +
          round(fuelTanks[i].weightValue, 1) +
          " | " +
          round(fuelTanks[i].loadArmX * fuelTanks[i].weightValue, 1) +
          " | " +
          round(fuelTanks[i].loadArmY * fuelTanks[i].weightValue, 1) +
          " | "
      );

      fuelEquMassSum += fuelTanks[i].weightValue;
      fuelEquMomxSum += fuelTanks[i].loadArmX * fuelTanks[i].weightValue;
      fuelEquMomySum += fuelTanks[i].loadArmY * fuelTanks[i].weightValue;
    }
    mdText_lst.push("|  |  |  |  |");
    mdText_lst.push(
      "| SUM          | **" +
        round(fuelEquMassSum, 1) +
        " kg** | **" +
        round(fuelEquMomxSum, 0) +
        " kg x m** | **" +
        round(fuelEquMomySum, 0) +
        " kg x m** | "
    );

    mdText_lst.push("");

    mdText_lst.push("### Passengers Loaded");
    let paxMassSum: number = 0;
    let paxMomxSum: number = 0;
    let paxMomySum: number = 0;
    mdText_lst.push("| ITEM          | MASS [kg] | MOMENT X [kg x m] | MOMENT Y [kg x m] | SEAT |");
    mdText_lst.push("|---------------|:---------:|:---------:|:---------:|:---------:|");
    for (let i = 0; i < pax.length; i++) {
      mdText_lst.push(
        "| " +
          pax[i].name +
          "| " +
          round(pax[i].weightValue, 1) +
          " | " +
          round(pax[i].loadArmX * pax[i].weightValue, 1) +
          " | " +
          round(pax[i].loadArmY * pax[i].weightValue, 1) +
          " | " +
          pax[i].parameters +
          "|"
      );

      paxMassSum += pax[i].weightValue;
      paxMomxSum += pax[i].loadArmX * pax[i].weightValue;
      paxMomySum += pax[i].loadArmY * pax[i].weightValue;
    }
    mdText_lst.push("|  |  |  |  |");
    mdText_lst.push(
      "| SUM          | **" +
        round(paxMassSum, 1) +
        " kg** | **" +
        round(paxMomxSum, 0) +
        " kg x m** | **" +
        round(paxMomySum, 0) +
        " kg x m** | "
    );

    mdText_lst.push("");

    mdText_lst.push("### Cargo Loaded");
    let cargoMassSum: number = 0;
    let cargoMomxSum: number = 0;
    let cargoMomySum: number = 0;

    let cargoMassIncCrewCargoSum: number = 0; // irrelevant for calc, only for report
    mdText_lst.push("| ITEM          | MASS [kg] | MOMENT X [kg x m] | MOMENT Y [kg x m] | ");
    mdText_lst.push("|---------------|:---------:|:---------:|:---------:|");
    for (let i = 0; i < cargo.length; i++) {
      mdText_lst.push(
        "| " +
          cargo[i].name +
          "| " +
          round(cargo[i].weightValue, 1) +
          " | " +
          round(cargo[i].loadArmX * cargo[i].weightValue, 1) +
          " | " +
          round(cargo[i].loadArmY * cargo[i].weightValue, 1) +
          " | "
      );

      cargoMassSum += cargo[i].weightValue;
      cargoMomxSum += cargo[i].loadArmX * cargo[i].weightValue;
      cargoMomySum += cargo[i].loadArmY * cargo[i].weightValue;

      cargoMassIncCrewCargoSum += cargo[i].weightValue; // irrelevant for calc, only for report
    }
    mdText_lst.push("|  |  |  |  |");
    mdText_lst.push(
      "| SUM          | **" +
        round(cargoMassSum, 1) +
        " kg** | **" +
        round(cargoMomxSum, 0) +
        " kg x m** | **" +
        round(cargoMomySum, 0) +
        " kg x m** | "
    );

    mdText_lst.push("");

    mdText_lst.push("### Crew Loaded");
    let crewCrewCargoMassSum: number = 0;
    let crewCrewCargoMomxSum: number = 0;
    let crewCrewCargoMomySum: number = 0;

    let crewMassExclCrewCargo: number = 0; // irrelevant for calc, only for report

    mdText_lst.push("| ITEM          | MASS [kg] | MOMENT X [kg x m] | MOMENT Y [kg x m] | ");
    mdText_lst.push("|---------------|:---------:|:---------:|:---------:|");
    for (let i = 0; i < pic.length; i++) {
      mdText_lst.push(
        "| " +
          pic[i].name +
          "| " +
          round(pic[i].weightValue, 1) +
          " | " +
          round(pic[i].loadArmX * pic[i].weightValue, 1) +
          " | " +
          round(pic[i].loadArmY * pic[i].weightValue, 1) +
          " | "
      );

      crewCrewCargoMassSum += pic[i].weightValue;
      crewCrewCargoMomxSum += pic[i].loadArmX * pic[i].weightValue;
      crewCrewCargoMomySum += pic[i].loadArmY * pic[i].weightValue;

      // only disblay value - irrelevant for report
      crewMassExclCrewCargo += pic[i].weightValue;
    }
    for (let i = 0; i < fo.length; i++) {
      mdText_lst.push(
        "| " +
          fo[i].name +
          "| " +
          round(fo[i].weightValue, 1) +
          " | " +
          round(fo[i].loadArmX * fo[i].weightValue, 1) +
          " | " +
          round(fo[i].loadArmY * fo[i].weightValue, 1) +
          " | "
      );

      crewCrewCargoMassSum += fo[i].weightValue;
      crewCrewCargoMomxSum += fo[i].loadArmX * fo[i].weightValue;
      crewCrewCargoMomySum += fo[i].loadArmY * fo[i].weightValue;

      // only disblay value - irrelevant for report
      crewMassExclCrewCargo += fo[i].weightValue;
    }
    for (let i = 0; i < crew.length; i++) {
      mdText_lst.push(
        "| " +
          crew[i].name +
          "| " +
          round(crew[i].weightValue, 1) +
          " | " +
          round(crew[i].loadArmX * crew[i].weightValue, 1) +
          " | " +
          round(crew[i].loadArmY * crew[i].weightValue, 1) +
          " | "
      );

      crewCrewCargoMassSum += crew[i].weightValue;
      crewCrewCargoMomxSum += crew[i].loadArmX * crew[i].weightValue;
      crewCrewCargoMomySum += crew[i].loadArmY * crew[i].weightValue;

      // only disblay value - irrelevant for report
      crewMassExclCrewCargo += crew[i].weightValue;
    }
    for (let i = 0; i < crewC.length; i++) {
      mdText_lst.push(
        "| " +
          crewC[i].name +
          "| " +
          round(crewC[i].weightValue, 1) +
          " | " +
          round(crewC[i].loadArmX * crewC[i].weightValue, 1) +
          " | " +
          round(crewC[i].loadArmY * crewC[i].weightValue, 1) +
          " | "
      );

      crewCrewCargoMassSum += crewC[i].weightValue;
      crewCrewCargoMomxSum += crewC[i].loadArmX * crewC[i].weightValue;
      crewCrewCargoMomySum += crewC[i].loadArmY * crewC[i].weightValue;

      cargoMassIncCrewCargoSum += crewC[i].weightValue; // irrelevant for calc, only for report
    }
    mdText_lst.push("|  |  |  |  |");
    mdText_lst.push(
      "| SUM          | **" +
        round(crewCrewCargoMassSum, 1) +
        " kg** | **" +
        round(crewCrewCargoMomxSum, 0) +
        " kg x m** | **" +
        round(crewCrewCargoMomySum, 0) +
        " kg x m** | "
    );

    let mdString: string = "";
    for (let i = 0; i < mdText_lst.length; i++) {
      mdString += mdText_lst[i] + "\n";
    }

    // replace values — catalog-dispatched
    const flightRowRead = classCom.namedQuery("flightlistByFlightId", { flightId });
    flightRowRead.catch(() => undefined);
    const revisionRowRead = classCom.namedQuery("fuelCountByFlightId", { flightId });
    revisionRowRead.catch(() => undefined);
    const flightNumber = (await flightRowRead)[0].flightNumber;
    const revision: string = String((await revisionRowRead)[0].n);
    const utcTimeNow = getUtcTime(new Date());
    const issueTime =
      padLeft(utcTimeNow.getHours().toString(), "0", 2) +
      ":" +
      padLeft(utcTimeNow.getMinutes().toString(), "0", 2) +
      " UTC";
    const estTimeDep: Date = new Date(
      Number(getParameterOfArray(flightId, "flightlist", property.estTimeDep, flightlistParams)[0]?.value ?? 0)
    );
    const estTimeDep_gui_LT =
      padLeft(estTimeDep.getHours().toString(), "0", 2) +
      ":" +
      padLeft(estTimeDep.getMinutes().toString(), "0", 2) +
      " LT";
    const estTimeDep_utc = getUtcTime(estTimeDep);
    const estTimeDep_gui_UTC =
      padLeft(estTimeDep_utc.getHours().toString(), "0", 2) +
      ":" +
      padLeft(estTimeDep_utc.getMinutes().toString(), "0", 2) +
      " UTC";

    const originName: string = getParameterOfArray(flightId, "flightlist", property.icaoOrigin, flightlistParams)[0]
      .value;
    const destName: string = getParameterOfArray(flightId, "flightlist", property.destName, flightlistParams)[0].value;
    const altName: string = getParameterOfArray(flightId, "flightlist", property.alternateName, flightlistParams)[0]
      .value;

    mdString = mdString.replace("{FlightNumber}", flightNumber);
    mdString = mdString.replace("{opsMissionType}", opsMissionTypeEnum[opsMissionType].toUpperCase());
    mdString = mdString.replace("{revision}", revision);
    mdString = mdString.replace("{issueTime}", issueTime);
    mdString = mdString.replace("{estTimeDep_gui_UTC}", estTimeDep_gui_UTC);
    mdString = mdString.replace("{estTimeDep_gui_LT}", estTimeDep_gui_LT);
    mdString = mdString.replace("{originName}", originName);
    mdString = mdString.replace("{destName}", destName);
    mdString = mdString.replace("{altName}", altName);

    const dryOperatingMass: string = getParameterOfArray(
      flightId,
      "loading",
      property.dryOperatingMass,
      loadingParams
    )[0].value;
    const fpp = new fppProvider();
    const emptyWeight: payLoadStruct[] = await fpp.getLoadingObjectsAircraftModel(
      acft.registration,
      payloadCodeTypeEnum.emptyWeight,
      classCom
    );
    const emptyMass: string = emptyWeight[0].weightValue.toString();
    const takeOffMass: string = getParameterOfArray(flightId, "loading", property.takeOffMass, loadingParams)[0].value;
    const landingMass: string = getParameterOfArray(flightId, "loading", property.estLandingMass, loadingParams)[0]
      .value;
    const zeroFuelMass: string = getParameterOfArray(flightId, "loading", property.zeroFuelMass, loadingParams)[0]
      .value;
    const emptyEquippedMass: string = getParameterOfArray(
      flightId,
      "loading",
      property.emptyEquippedMass,
      loadingParams
    )[0].value;

    mdString = mdString.replace("{aircraftEmptyMass}", emptyMass);
    mdString = mdString.replace("{dom}", dryOperatingMass);
    mdString = mdString.replace("{takeoffMass}", takeOffMass);
    mdString = mdString.replace("{landingMass}", landingMass);
    mdString = mdString.replace("{zeroFuelMass}", zeroFuelMass);
    mdString = mdString.replace("{emptyEquippedMass}", emptyEquippedMass);
    // calc all sorts of values

    // dry operating mass

    // mdString = mdString.replace("{dom}", round(dryOperatingMass, 0).toString())

    mdString = mdString.replace("{crewMassExclCrewCargo}", round(crewMassExclCrewCargo, 0).toString());
    mdString = mdString.replace("{paxWeight}", round(paxMassSum, 0).toString());
    mdString = mdString.replace("{cargoWeightIncCrewCargo}", round(cargoMassIncCrewCargoSum, 0).toString());

    return alignMarkdownTablesInDocument(mdString);
  }

  async getProcessResultState(flightId: string, opsMissionType: opsMissionTypeEnum, classCom: any): Promise<string> {
    const result = await classCom.namedQuery("loadingProcessResultStateByFlight", { flightId, opsMissionType });

    if (result.length == 0) {
      return "NIL";
    }

    if (result[0].processResultState == 0) {
      return "N/A";
    }

    if (result[0].processResultState == 1) {
      return "OK";
    }

    if (result[0].processResultState == 2) {
      return "WARN";
    }

    if (result[0].processResultState == 3) {
      return "FAIL";
    }

    return "N/A";
  }
  async getLoadAndBalanceReport(
    flightId: string,
    opsMissionType: opsMissionTypeEnum,
    classCom: any,
    returnMarkdown = false
  ): Promise<string> {
    const result = await classCom.namedQuery("loadingProcessReportByFlight", { flightId, opsMissionType });

    // No row means the W&B run never completed (initMassAndBalance rolls back
    // and returns false on any fault). Say that, instead of dereferencing
    // result[0] and reporting "Cannot read properties of undefined (reading
    // 'processReport')" — which named the symptom and sent everyone looking at
    // the report code rather than at the aborted calculation.
    if (result.length === 0) {
      throw new Error(
        `no live mass and balance result for flight ${flightId} (mission ${opsMissionTypeEnum[opsMissionType]}) — ` +
          "the calculation did not complete, or its row was superseded"
      );
    }

    if (returnMarkdown) {
      return result[0].processReport;
    }

    const mdp = new markDownProcessing();

    const html: string = await mdp.convMarkdown(
      result[0].processReport,
      classCom.appearance,
      flightId,
      opsMissionType,
      classCom,
      this,
      result[0].signatureId,
      result[0].resultJson
    );

    if (classCom.targetPlatform == targetPlatformEnum.server) {
      const fs = classCom.package_fs;
      fs.writeFile("out.html", html, function (err) {
        if (!err) {
          const open = classCom.package_open;
          open("out.html");
        }
      });
    }

    return html;
  }
  // Row-to-struct conversion of getLoadingObjectOfFlight.
  private buildLoadingPayload(flightId: string, pArray: any[]): payLoadStruct {
    const retPayload: payLoadStruct = new payLoadStruct();
    retPayload.codeType =
      payloadCodeTypeEnum[getParameterOfArray(flightId, "loadingElements", property.codeType, pArray)[0].value];
    retPayload.name = getParameterOfArray(flightId, "loadingElements", property.codeDescr, pArray)[0].value;
    retPayload.loadArmX = Number(
      getParameterOfArray(flightId, "loadingElements", property.valLoadArmX, pArray)[0].value
    );
    retPayload.loadArmY = Number(
      getParameterOfArray(flightId, "loadingElements", property.valLoadArmY, pArray)[0].value
    );
    retPayload.weightValue = Number(
      getParameterOfArray(flightId, "loadingElements", property.weightValue, pArray)[0].value
    );

    const paramSeat = getParameterOfArray(flightId, "loadingElements", property.txtSeatPosition, pArray);

    if (paramSeat.length > 0) {
      retPayload.parameters = `positionName:${paramSeat[0].value};`;
    }
    const duty = getParameterOfArray(flightId, "loadingElements", property.codeDuty, pArray)[0]?.value;
    if (duty != null) retPayload.parameters = `${retPayload.parameters ?? ""}duty:${duty};`;

    return retPayload;
  }

  /** The elements of one calculation result, per requested code type. */
  loadingObjectsOf(
    elements: CalcElement[],
    flightId: string,
    payloadCodeTypes: payloadCodeTypeEnum[]
  ): payLoadStruct[][] {
    return payloadCodeTypes.map((codeType) =>
      elements
        .filter((element) => element.payLoadCodeType == codeType)
        .map((element) =>
          this.buildLoadingPayload(
            flightId,
            Object.entries(element.values).map(([key, value]) => ({ parameter: Number(key), value }))
          )
        )
    );
  }

  async getLoadingObjectOfFlight(
    flightId: string,
    opsMissionType: opsMissionTypeEnum,
    payloadCodeType: payloadCodeTypeEnum,
    classCom: any
  ): Promise<payLoadStruct[]> {
    const payLoad_lst: payLoadStruct[] = [];

    // The elements of the live mass and balance version, from its result.
    // A version without a document (an older build) reads the element rows.
    const live = await liveCalc(classCom, "loading", flightId, opsMissionType, getParametersArray);
    if (live?.elements) {
      return this.loadingObjectsOf(live.elements, flightId, [payloadCodeType])[0];
    }

    const loadingElements_tbl = await classCom.namedQuery("loadingelementsByFlightMissionType", {
      flightId,
      opsMissionType,
      payLoadCodeType: payloadCodeType,
    });

    const paraHash_lst: string[] = [];
    for (let i = 0; i < loadingElements_tbl.length; i++) {
      paraHash_lst.push(loadingElements_tbl[i].parameterHash);
    }

    const pArrayByHash = await getParametersArrayBulk(paraHash_lst, "loadingElements", opsMissionType);

    for (let i = 0; i < loadingElements_tbl.length; i++) {
      const pArray = pArrayByHash.get(paraHash_lst[i]) ?? [];
      payLoad_lst.push(this.buildLoadingPayload(flightId, pArray));
    }

    return payLoad_lst;
  }

  async getPerfDiagramData(
    perfResult: perfResultsEnum,
    acftModel: string,
    classCom: any,
    flightId: string,
    opsMissionType: opsMissionTypeEnum,
    _filterConditions?: string[],
    _perfParameters?: string,
    resultJson?: string
  ): Promise<svgDiagramDataStruct[]> {
    // The figures of the report version that is shown.
    const loadingParams = await shownParams(
      resultJson,
      (result) => result.values,
      () => getParametersArray(flightId, "loading", opsMissionType)
    );
    // empty equipped
    const emptyEquippedMass = Number(
      getParameterOfArray(flightId, "loading", property.emptyEquippedMass, loadingParams)[0].value
    );
    const emptyEuippedcenterOfGravityX = Number(
      getParameterOfArray(flightId, "loading", property.emptyEuippedcenterOfGravityX, loadingParams)[0].value
    );
    const emptyEuippedcenterOfGravityY = Number(
      getParameterOfArray(flightId, "loading", property.emptyEuippedcenterOfGravityY, loadingParams)[0].value
    );

    // Every one of the five lookups below indexes by this same axis triple —
    // shared, not just identical, since none of them is ever mutated.
    const keys: string[] = ["grossWeight", "centerOfGravityLon", "centerOfGravityLat"];
    let values: number[] = [emptyEquippedMass, emptyEuippedcenterOfGravityX, emptyEuippedcenterOfGravityY];
    let desig: string[] = ["EEM"];

    const fpp = new fppProvider();
    // Kicked off before the try so a rejection here still propagates out of
    // the method rather than being swallowed by the catch below. The no-op
    // .catch keeps an early rejection from being reported as unhandled while
    // the other four lookups are still being started.
    const eewPromise = fpp.getPerfModelDiagram(acftModel, perfResult, classCom, keys, values, desig, false);
    eewPromise.catch(() => {});

    try {
      // zero fuel
      const zeroFuelMass = Number(
        getParameterOfArray(flightId, "loading", property.zeroFuelMass, loadingParams)[0].value
      );
      const zeroFuelcenterOfGravityX = Number(
        getParameterOfArray(flightId, "loading", property.zeroFuelcenterOfGravityX, loadingParams)[0].value
      );
      const zeroFuelcenterOfGravityY = Number(
        getParameterOfArray(flightId, "loading", property.zeroFuelcenterOfGravityY, loadingParams)[0].value
      );

      values = [zeroFuelMass, zeroFuelcenterOfGravityX, zeroFuelcenterOfGravityY];
      desig = ["ZFM"];

      const zfwPromise = fpp.getPerfModelDiagram(acftModel, perfResult, classCom, keys, values, desig, true);
      // The try can throw before Promise.all below; keep a later rejection from
      // reaching the global handler (Sentry) as unhandled.
      zfwPromise.catch(() => {});

      // takeoff
      const takeOffMass = Number(
        getParameterOfArray(flightId, "loading", property.takeOffMass, loadingParams)[0].value
      );
      const takeOffcenterOfGravityX = Number(
        getParameterOfArray(flightId, "loading", property.takeOffcenterOfGravityX, loadingParams)[0].value
      );
      const takeOffcenterOfGravityY = Number(
        getParameterOfArray(flightId, "loading", property.takeOffCenterOfGravityY, loadingParams)[0].value
      );

      values = [takeOffMass, takeOffcenterOfGravityX, takeOffcenterOfGravityY];
      desig = ["TOM"];

      const toPromise = fpp.getPerfModelDiagram(acftModel, perfResult, classCom, keys, values, desig, true);
      toPromise.catch(() => {});

      // landing
      const estLandingMass = Number(
        getParameterOfArray(flightId, "loading", property.estLandingMass, loadingParams)[0].value
      );
      const estLandingCenterOfGravityXVal = getParameterOfArray(
        flightId,
        "loading",
        property.estLandingCenterOfGravityX,
        loadingParams
      )[0]?.value;
      if (estLandingCenterOfGravityXVal === undefined) {
        throw new Error("loading data not yet computed: estLandingCenterOfGravityX");
      }
      const estLandingCenterOfGravityX = Number(estLandingCenterOfGravityXVal);
      const estLandingCenterOfGravityY = Number(
        getParameterOfArray(flightId, "loading", property.estLandingCenterOfGravityY, loadingParams)[0].value
      );

      values = [estLandingMass, estLandingCenterOfGravityX, estLandingCenterOfGravityY];
      desig = ["LDM"];

      const ldgPromise = fpp.getPerfModelDiagram(acftModel, perfResult, classCom, keys, values, desig, true);
      ldgPromise.catch(() => {});

      // dry operating weight
      const dryOperatingMass = Number(
        getParameterOfArray(flightId, "loading", property.dryOperatingMass, loadingParams)[0].value
      );
      const dryCgPositionX = Number(
        getParameterOfArray(flightId, "loading", property.dryCgPositionX, loadingParams)[0].value
      );
      const dryCgPositionY = Number(
        getParameterOfArray(flightId, "loading", property.dryCgPositionY, loadingParams)[0].value
      );

      values = [dryOperatingMass, dryCgPositionX, dryCgPositionY];
      desig = ["DOM"];

      const dowPromise = fpp.getPerfModelDiagram(acftModel, perfResult, classCom, keys, values, desig, true);
      dowPromise.catch(() => {});

      const [eew, zfw, to, ldg, dow] = await Promise.all([eewPromise, zfwPromise, toPromise, ldgPromise, dowPromise]);

      const eewPos = eew[0].points[0].position;

      let zfwPos;
      if (zfw[0].points != undefined) zfwPos = zfw[0].points[0].position;
      if (zfw[0].points != undefined) eew[0].points.push(zfw[0].points[0]);

      let toPos;
      if (to[0].points !== undefined) {
        eew[0].points.push(to[0].points[0]);
        toPos = to[0].points[0].position;
      }

      let ldgPos;
      if (ldg[0].points != undefined) ldgPos = ldg[0].points[0].position;
      if (ldg[0].points != undefined) eew[0].points.push(ldg[0].points[0]);

      let dowPos;
      if (dow[0].points != undefined) dowPos = dow[0].points[0].position;

      if (dow[0].points != undefined) eew[0].points.push(dow[0].points[0]);

      eew[0].lines = [];

      const qlinethin: svgLineStruct = { lineWidth: 4, points: [zfwPos, dowPos, eewPos], lineColor: undefined };
      const qline: svgLineStruct = { lineWidth: 6, points: [toPos, ldgPos, zfwPos], lineColor: undefined };

      if (classCom.appearance == appearanceEnum.dark) {
        qlinethin.lineColor = "#ff00ff";
        qline.lineColor = "#ff00ff";
      } else {
        qlinethin.lineColor = "#ff00ff";
        qline.lineColor = "#ff00ff";
      }

      eew[0].lines.push(qlinethin);
      eew[0].lines.push(qline);

      const arr: any[] = [];
      arr.push(zfw);
      arr.push(ldg);
      arr.push(dow);
      // arr.push(eew) empty equipped does not impact
      arr.push(to);
      let violation: boolean = false;
      let annu: string = "";
      for (let i = 0; i < arr.length; i++) {
        if (arr[i][0]._violation) {
          violation = true;
          if (!annu.includes(arr[i][0]._annunciator)) {
            annu += arr[i][0]._annunciator;
          }
        }
      }

      eew[0]._violation = violation;
      eew[0]._annunciator = annu;

      return eew;
    } catch (err) {
      console.log(err + " could not get perf matrix data");
    }

    // A rejection of the eew lookup itself was never caught by the try above
    // in the sequential version, since it was awaited before the try began.
    // Awaiting it again here (already settled) preserves that: on rejection
    // this throws and propagates out of the method uncaught; on resolution it
    // returns the same eew array the try block would have started from.
    const eew = await eewPromise;
    eew[0]._violation = true;
    eew[0]._annunciator = "MATRIX INTERPR ERROR!";

    return eew;
  }

  async checkProcessResult(
    acftModel: string,
    classCom: any,
    flightId: string,
    opsMissionType: opsMissionTypeEnum,
    givenLoadingParams?: parameterStruct[]
  ): Promise<processResultStateEnum> {
    const loadingParams = givenLoadingParams ?? (await getParametersArray(flightId, "loading", opsMissionType));
    // takeOffMass
    let takeOffMass = Number(getParameterOfArray(flightId, "loading", property.takeOffMass, loadingParams)[0].value);
    let takeOffMassCenterOfGravityX = Number(
      getParameterOfArray(flightId, "loading", property.takeOffcenterOfGravityX, loadingParams)[0].value
    );
    let takeOffMassCenterOfGravityY = Number(
      getParameterOfArray(flightId, "loading", property.takeOffCenterOfGravityY, loadingParams)[0].value
    );

    takeOffMass = Math.ceil(takeOffMass / 5) * 5;
    takeOffMassCenterOfGravityX = round(Math.ceil(takeOffMassCenterOfGravityX / 0.005) * 0.005, 3);
    takeOffMassCenterOfGravityY = round(Math.ceil(takeOffMassCenterOfGravityY / 0.005) * 0.005, 3);

    const keys: string[] = ["grossWeight", "centerOfGravityLon", "centerOfGravityLat"];
    const values: number[] = [takeOffMass, takeOffMassCenterOfGravityX, takeOffMassCenterOfGravityY];

    // Only the violation flag is read, so the chart images are not loaded.
    // They are 1-2 MB each, and this check runs on every recalculation.
    const fpp = new fppProvider();
    const [lat, lon]: [svgDiagramDataStruct[], svgDiagramDataStruct[]] = await Promise.all([
      fpp.getPerfModelDiagram(
        acftModel,
        perfResultsEnum.withinLoadAndBalanceEnvelope_lateral,
        classCom,
        keys,
        values,
        ["TOM"],
        true
      ),
      fpp.getPerfModelDiagram(
        acftModel,
        perfResultsEnum.withinLoadAndBalanceEnvelope_longitudinal,
        classCom,
        keys,
        values,
        ["TOM"],
        true
      ),
    ]);

    if (lat[0]._violation || lon[0]._violation) {
      return processResultStateEnum.fail;
    } else {
      return processResultStateEnum.ok;
    }
  }

  createFormattedLoadingMarkdown(opsMissionType: opsMissionTypeEnum, payloadItems: payLoadStruct[]): string {
    const crewRoles = [
      payloadCodeTypeEnum.pic.toString(),
      payloadCodeTypeEnum.fo.toString(),
      payloadCodeTypeEnum.crew.toString(),
    ];
    const paxRoles = [payloadCodeTypeEnum.pax.toString()];
    const equipmentAndCargo = [
      payloadCodeTypeEnum.equipment.toString(),
      payloadCodeTypeEnum.optionalEquipment.toString(),
      payloadCodeTypeEnum.cargo.toString(),
    ];

    const typeAbbrev: { [key: string]: string } = {
      [payloadCodeTypeEnum.equipment]: "EQP",
      [payloadCodeTypeEnum.optionalEquipment]: "OPT",
      [payloadCodeTypeEnum.cargo]: "CRG",
      [payloadCodeTypeEnum.pax]: "PAX",
      [payloadCodeTypeEnum.pic]: "PIC",
      [payloadCodeTypeEnum.fo]: "FO",
      [payloadCodeTypeEnum.crew]: "CRW",
    };

    const opsMissionKey = opsMissionTypeEnum[opsMissionType];

    const header = `## LOAD CONFIGURATION REPORT\n`;
    const crewSection = [
      `### CREW & PASSENGERS\n`,
      "```plaintext",
      `NAME                        CODE   MASS[kg]   ACTV`,
      `--------------------------  -----  ---------  -----`,
    ];

    const cargoSection = [
      `\n### EQUIPMENT & CARGO\n`,
      "```plaintext",
      `ITEM                        TYPE   MASS[kg]   ACTV`,
      `--------------------------  -----  ---------  -----`,
    ];

    let totalWeight = 0;
    let totalMomentX = 0;
    let totalMomentY = 0;
    let activeCount = 0;

    for (const item of payloadItems) {
      const opsType = parseCss(item.parameters, "opsMissionType");
      const hasOpsType = typeof opsType === "string" && opsType.trim().length > 0;
      if (hasOpsType && opsType !== opsMissionKey) continue;

      const name = item.name.padEnd(26).substring(0, 26);
      const roleKey = item.codeType.toString();
      const role = typeAbbrev[item.codeType] || "UNK";
      const weight = `${item.weightValue.toFixed(1).padStart(7)}`;
      const status = item.active ? "Y" : "N";

      if (item.active) {
        totalWeight += item.weightValue;
        totalMomentX += item.weightValue * item.loadArmX;
        totalMomentY += item.weightValue * item.loadArmY;
        activeCount++;
      }

      const line = `${item.active ? name : `~~${name}~~`}  ${item.active ? role.padEnd(5) : `~~${role.padEnd(5)}~~`}  ${
        item.active ? weight : `~~${weight}~~`
      }    ${status}`;

      if (crewRoles.includes(roleKey) || paxRoles.includes(roleKey)) {
        crewSection.push(line);
      } else if (equipmentAndCargo.includes(roleKey)) {
        cargoSection.push(line);
      }
    }

    crewSection.push("```");
    cargoSection.push("```");

    const summary = [
      `\n### SUMMARY`,
      "```plaintext",
      `TOTAL ACTIVE: ${activeCount}    TOTAL MASS: ${totalWeight.toFixed(1)} kg`,
      `CG X: ${(totalMomentX / totalWeight).toFixed(2)} m   CG Y: ${(totalMomentY / totalWeight).toFixed(2)} m`,
      "```",
    ];

    return [header, ...crewSection, ...cargoSection, ...summary].join("\n");
  }

  generateMarkdownOFPWinchMassProfile(
    missionResults: Array<{ key: string; value: number }>,
    estimatedDistanceMeters: number,
    fuelLoading: number,
    extraWeight: number,
    personsAboard: number,
    aircraftType: string,
    aircraftReg: string,
    date: string, // e.g. "01 JUN 2025"
    timeUTC: string // e.g. "14:32Z"
  ): string {
    const content: string[] = [];
    const estimatedDistanceNM = (estimatedDistanceMeters / 1852).toFixed(1);

    content.push("```");

    // Header block with aligned spacing
    content.push(`DATE       : ${date.padEnd(22)}TIME        : ${timeUTC}`);
    content.push(`AIRCRAFT   : ${`${aircraftType} ${aircraftReg}`.padEnd(32)}`);
    content.push("");

    // Flight parameters
    content.push("FLIGHT PARAMETERS");
    content.push(`  Fuel Loading    ${". ".repeat(12)}${fuelLoading.toFixed(1).padStart(8)} KG`);
    content.push(`  Extra Weight    ${". ".repeat(12)}${extraWeight.toFixed(1).padStart(8)} KG`);
    if (personsAboard !== undefined) {
      content.push(`  Persons Aboard  ${". ".repeat(12)}${personsAboard.toString().padStart(8)} PAX`);
    }
    content.push("");

    // Mission mass table
    content.push("MISSION TYPE            " + ". ".repeat(8) + "    MAX MASS (KG)");
    content.push("------------------                          -------------");

    for (const item of missionResults) {
      const label = item.key.toUpperCase().padEnd(24);
      const dots = ". ".repeat(8);
      const mass = item.value.toFixed(2).padStart(10);
      content.push(`${label}${dots}${mass}`);
    }

    content.push("");
    content.push("NOTE: MASS VALUES ABOVE ARE MAXIMUMS UNDER PRELOADING CONFIGURATION.");
    content.push("      VALID ONLY FOR CURRENT MISSION TYPES AND CONFIGURED LOAD STATE.");
    content.push("```");

    return "## WINCH MASS PROFILE\n\n" + content.join("\n");
  }
}
